import { useEffect } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { isEmpty } from '@iterable/utils';
import { addNegativeToast } from 'modules/toastContainer';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { MediumOptions, QueryValueOptionsByParam, Tabs } from '../constants';
import { Metric, QueryParamErrors, QueryParams } from '../types';
import { UrlFilterErrorToast } from '../components/UrlFilterErrorToast';
import { filterSelectedMetricsByMedium } from '../utils';

const sortParams = ['campaignsSortBy', 'campaignsSortDir', 'audienceSortBy', 'audienceSortDir'];
/**
 * maps a string arr to a complete lower case version,
 * which we can then use to index query param/values in a case-agnostic way
 * [AbC, dEf] => {abc: AbC, def: dEf}
 * */
const caseInsensitiveMap = (options: string[]): Record<string, string> =>
  options.reduce((acc, opt) => {
    return { ...acc, [opt.toLocaleLowerCase()]: opt };
  }, {});

// Check if datetime string representation is in ISO 8601 Format
const isIsoDate = (str: string) => {
  return /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{1,3}Z/.test(str);
};

// Classify the different query params as time-based (e.g. startTime, timePreset) or id-based (e.g. campaignIds)
const isTimeBased = (param: string) => {
  const lowerCase = param.toLocaleLowerCase();
  return lowerCase.includes('time') && !lowerCase.includes('timeseries');
};

// Adding encoding as 'en-US' in the below isIdBased function, because this check can fail if the browser is set to another language/encoding
const isIdBased = (param: string) => param.toLocaleLowerCase('en-US').includes('id');

// Validate time-based values and those in general that present as a string
const validateSingleStringValues = (
  param: string,
  valueArr: string[],
  valueOptions: Record<string, string[]>,
) => {
  if (valueArr.length !== 1) {
    return ['', [`${valueArr} - only 1 value can be provided`]];
  }
  const val = valueArr[0];
  if (param === 'segment') {
    return [val, ''];
  }
  if (['timezone', 'timePreset', 'tab', 'region', ...sortParams].includes(param)) {
    const validatedStr = caseInsensitiveMap(valueOptions[param])[
      decodeURIComponent(val).toLocaleLowerCase()
    ];
    return validatedStr ? [validatedStr, ''] : ['', [val]];
  }

  // validate start and end times
  if (!isIsoDate(val)) {
    return ['', [`${val} - datetimes should be in ISO 8601 format YYYY-MM-DDTHH:MN:SS.MSSZ`]];
  }
  return [val, ''];
};

// General fn used to help split an array into separate arrays containing invalid and valid values
const partition = <T,>(arr: T[], condition: (e: T) => boolean) => {
  const trues = arr.filter((el) => condition(el));
  const falses = arr.filter((el) => !condition(el));
  return [trues, falses];
};

// Reformat all query values into those expected by MI filters
const formatValues = (valid: string[], param: string, valueOptions: Record<string, string[]>) => {
  if (!valid.length) {
    return '';
  }
  if (isIdBased(param)) {
    return valid.map((v) => parseInt(v, 10));
  }
  const availableOpts = caseInsensitiveMap(valueOptions[param]);
  return valid.map((v) => availableOpts[v.toLocaleLowerCase()]);
};

// Validate the values passed in for each query param
const validateValues = (param: string, val: string[], valueOptions: Record<string, string[]>) => {
  let valid;
  let invalid;
  if (isTimeBased(param) || ['tab', 'segment', 'region', ...sortParams].includes(param)) {
    [valid, invalid] = validateSingleStringValues(param, val, valueOptions);
    return { valid, invalid };
  }
  if (isIdBased(param)) {
    [valid, invalid] = partition(val, (x) => !Number.isNaN(Number(x)) && Number(x) >= 0);
  } else {
    const availableOpts = caseInsensitiveMap(valueOptions[param]);
    [valid, invalid] = partition(val, (x) => !!availableOpts[x.toLocaleLowerCase()]);
  }
  return { valid: formatValues(valid, param, valueOptions), invalid };
};

// Validate all query params
export const validateQueryParams = (
  queryParams: URLSearchParams,
  selectedMediums: string[],
  campaignAnalyticsParamValueOptions: Record<string, string[]> | undefined = undefined,
  highLevelMetrics: Metric[],
  analyticsMetrics: Metric[],
) => {
  const valueOptions = campaignAnalyticsParamValueOptions || QueryValueOptionsByParam;
  const availParamNames = caseInsensitiveMap(valueOptions.params);
  const validatedQueryParams: QueryParams = {};
  const invalidParamList: string[] = [];
  const invalidValsByParam: { [param: string]: string | string[] } = {};

  queryParams.forEach((value, param) => {
    const val = value.split(',');
    const validParam = availParamNames[param.toLocaleLowerCase()];
    if (validParam) {
      if (validParam !== 'previews' && validParam !== 'projectId') {
        const { valid, invalid } = validateValues(validParam, val, valueOptions);
        if (valid) {
          // @ts-ignore
          validatedQueryParams[validParam] = valid;
        }
        if (invalid.length > 0) {
          invalidValsByParam[validParam] = invalid;
        }
      }
    } else {
      invalidParamList.push(param);
    }
  });

  const { endTime, startTime, metrics, messageMediums, tab } = validatedQueryParams;
  const mediums = messageMediums || selectedMediums;
  if (endTime) {
    if (!startTime) {
      delete validatedQueryParams.endTime;
      invalidValsByParam.endTime = ['a valid startTime must be provided to set an endTime'];
    } else if (!moment(endTime).isAfter(moment(startTime))) {
      delete validatedQueryParams.endTime;
      invalidValsByParam.endTime = ['endTime must be after startTime'];
    }
  }

  if (metrics) {
    const filteredMetrics = filterSelectedMetricsByMedium(
      mediums,
      metrics,
      highLevelMetrics,
      analyticsMetrics,
    );
    validatedQueryParams.metrics = filteredMetrics;
  }

  if (tab) {
    if (
      !(mediums as string[]).includes(MediumOptions.Email) &&
      [Tabs.location, Tabs.devices].includes(tab as string)
    ) {
      delete validatedQueryParams.tab;
      invalidValsByParam.tab = ['Email must be a selected medium for the Location and Device tabs'];
    }
  }

  return {
    validatedQueryParams,
    errors: {
      invalidParamList,
      invalidValsByParam,
    },
  };
};

// Parses filter params from url and returns them as an object of the form: {[paramName]: [paramValue]}
export const useGetValidatedUrlParams = (selectedMediums: string[]) => {
  const rawQueryParams = useQueryParams();
  const dispatch = useDispatch();
  const { analyticsMetrics, highLevelMetrics } = useMetricsContext();

  const { validatedQueryParams, errors } = validateQueryParams(
    rawQueryParams,
    selectedMediums,
    undefined,
    highLevelMetrics,
    analyticsMetrics,
  );

  // generate an error toast if any query params/values are invalid
  useEffect(() => {
    if (errors.invalidParamList.length || !isEmpty(errors.invalidValsByParam)) {
      dispatch(addNegativeToast(<UrlFilterErrorToast errors={errors as QueryParamErrors} />));
    }
  }, []);

  return validatedQueryParams;
};
